import { useMemo, useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  audience: "funders" | "agencies";
}

export default function DemoRequestForm({ audience }: Props) {
  const { user } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [website, setWebsite] = useState("");
  const [focus, setFocus] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const valid = useMemo(
    () =>
      consent &&
      orgName.trim().length >= 2 &&
      contactName.trim().length >= 2 &&
      /.+@.+\..+/.test(contactEmail) &&
      orgName.length <= 200 &&
      contactName.length <= 120 &&
      contactEmail.length <= 255 &&
      website.length <= 300 &&
      focus.length <= 1500,
    [consent, orgName, contactName, contactEmail, website, focus],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please complete the required fields and acknowledge consent.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("partner_applications").insert({
      applicant_user_id: user?.id ?? null,
      org_name: orgName.trim(),
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      website: website.trim() || null,
      tier_requested: "anchor",
      services: [],
      counties: [],
      why: focus.trim() || null,
      source: audience === "funders" ? "institutional_funder" : "institutional_agency",
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not submit request", { description: error.message });
      return;
    }
    setDone(true);
    toast.success("Request received. Our team will be in touch within 2 business days.");
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
        <h3 className="font-display text-xl font-semibold text-foreground mb-1">
          Thanks — we'll be in touch.
        </h3>
        <p className="text-sm text-muted-foreground">
          A TEM analyst will reach out to {contactEmail} within two business days to schedule your
          walkthrough and provision sample data access.
        </p>
      </div>
    );
  }

  const audienceLabel = audience === "funders" ? "foundation" : "agency";

  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5">
      <div className="flex items-center gap-2 text-primary text-sm font-medium">
        <Mail className="h-4 w-4" /> Request a walkthrough &amp; sample report
      </div>
      <p className="text-sm text-muted-foreground -mt-3">
        Tell us about your {audienceLabel}. We'll send a sample Alabama intelligence briefing and
        book a 30-minute walkthrough — no obligation.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="df-org">Organization *</Label>
          <Input
            id="df-org"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            maxLength={200}
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="df-web">Website</Label>
          <Input
            id="df-web"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://"
            maxLength={300}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="df-name">Your name *</Label>
          <Input
            id="df-name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            maxLength={120}
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="df-email">Work email *</Label>
          <Input
            id="df-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            maxLength={255}
            required
            className="mt-1.5"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="df-focus">
          What are you trying to understand? <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Textarea
          id="df-focus"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          maxLength={1500}
          placeholder={
            audience === "funders"
              ? "e.g. Where Health & Wellness dollars are concentrating across the Black Belt, or which sectors have unmet demand."
              : "e.g. DBE pipeline readiness across counties we serve, or which legislative changes affect our program."
          }
          className="mt-1.5 min-h-[96px]"
        />
      </div>

      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer rounded-md border border-border bg-muted/30 p-3">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          className="mt-0.5"
          aria-label="Consent to be contacted"
        />
        <span>
          I'm authorized to request access on behalf of my organization and consent to TEM
          contacting me about this request. I understand that any preview data shared is
          de-identified aggregate intelligence governed by the{" "}
          <a href="/privacy" target="_blank" className="underline hover:text-foreground">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="/terms" target="_blank" className="underline hover:text-foreground">
            Terms of Service
          </a>
          , and that full Intelligence-tier access requires a signed license. *
        </span>
      </label>

      <Button type="submit" size="lg" disabled={submitting || !valid} className="w-full sm:w-auto">
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Request walkthrough
      </Button>
    </form>
  );
}